import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { ReadScimSettings } from '@tinct/core-api-models';
import { ApiError, tinctApi } from '../../api';
import { invalidateScimQueries } from './invalidate-scim-queries';

export type UpdateScimEnablementParams = {
  organizationId: string;
  /** `true` lets the identity provider manage the members, `false` refuses its requests. */
  enabled: boolean;
};

export const updateScimEnablement = async (
  params: UpdateScimEnablementParams,
): Promise<ReadScimSettings> => {
  return params.enabled
    ? await tinctApi.scim.enableScim(params.organizationId)
    : await tinctApi.scim.disableScim(params.organizationId);
};

/**
 * Switches SCIM provisioning on or off. The API is the gate: 409 (5701) while a prerequisite is
 * unmet, its message says which. Switching it off deprovisions nobody.
 */
export const useUpdateScimEnablement = (
  options?: Omit<
    UseMutationOptions<ReadScimSettings, ApiError, UpdateScimEnablementParams>,
    'mutationFn'
  >,
) => {
  const { onSuccess, ...restOptions } = options || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateScimEnablement,
    onSuccess: (...args) => {
      invalidateScimQueries(queryClient, args[1].organizationId, 'settings');
      // The switch is also the organization's `scimStatus`, which every member's card reads
      queryClient.invalidateQueries({ queryKey: ['organization', args[1].organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      onSuccess?.(...args);
    },
    ...restOptions,
  });
};
